import { useMemo, useState } from 'react';
import type { AppSession, ImportResult, PracticeView } from '../../lib/types';
import { ApiError, api, type NewPatient } from '../../lib/api';
import { parsePatientLines } from '../../lib/csv';
import { Card } from '../../components/Bits';
import { LinkBox, StatusBadge } from './ManagerPatients';

const EXAMPLE = 'Linda Chen, linda.chen@example.com\nDavid Park, david.park@example.com';

/** Load patients onto the practice's list, one at a time or pasted from a spreadsheet, and invite them. */
export function AddPatientsPage({ session, practice, demo, onChanged, say }: { session: AppSession; practice: PracticeView; demo: boolean; onChanged: () => Promise<void>; say: (s: string) => void }) {
  const doctors = practice.roster.filter((r) => r.role === 'doctor');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [doctor, setDoctor] = useState(doctors[0]?.personId ?? '');
  const [invite, setInvite] = useState(true);
  const [seed, setSeed] = useState(demo);
  const [bulk, setBulk] = useState('');
  const parsed = useMemo(() => parsePatientLines(bulk), [bulk]);
  const [busy, setBusy] = useState<'one' | 'many' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);

  const submit = async (patients: Array<{ name: string; email: string }>, which: 'one' | 'many') => {
    setBusy(which);
    setError(null);
    try {
      const body: NewPatient[] = patients.map((p) => ({ ...p, ...(doctor ? { doctorId: doctor } : {}), ...(demo && seed ? { seedDemo: true } : {}) }));
      const r = await api.importPatients(body, invite, session.token);
      setResult(r);
      const emailed = r.invited.filter((i) => i.email.sent).length;
      say(`${r.added.length} added${invite ? `, ${emailed} emailed` : ''}${r.skipped.length > 0 ? `, ${r.skipped.length} skipped` : ''}.`);
      if (which === 'one') {
        setName('');
        setEmail('');
      } else {
        setBulk('');
      }
      await onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not add them.');
    } finally {
      setBusy(null);
    }
  };

  const oneValid = name.trim().length > 0 && /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim());

  return (
    <>
      <header className="desk-head">
        <div>
          <h1>Add patients</h1>
          <span className="hint">Each patient can create an account only with the email address you enter here.</span>
        </div>
        <a className="quiet small" href="#/practice">
          Back to the patient list
        </a>
      </header>

      {doctors.length === 0 ? (
        <Card tone="gold">
          <h2>Add a doctor first</h2>
          <p>Every patient is enrolled under one of your doctors. <a href="#/practice/roster">Add a doctor to the care team</a>, then come back.</p>
        </Card>
      ) : null}

      <Card>
        <div className="options">
          <label className="field">
            <span>Their doctor</span>
            <select value={doctor} onChange={(e) => setDoctor(e.target.value)}>
              {doctors.map((d) => (
                <option key={d.personId} value={d.personId}>
                  {d.name}
                </option>
              ))}
            </select>
          </label>
          <label className="check">
            <input type="checkbox" checked={invite} onChange={(e) => setInvite(e.target.checked)} /> Send the invitation email now
          </label>
          {demo ? (
            <label className="check">
              <input type="checkbox" checked={seed} onChange={(e) => setSeed(e.target.checked)} /> Give them two weeks of sample history when they enroll (demo)
            </label>
          ) : null}
        </div>
        <p className="hint small">
          {invite ? 'Invited patients become eligible at once and get an email with a one-time link.' : 'Patients stay pending until you invite them from the patient list.'}
          {demo ? ' Addresses on example.com are demo addresses: they are never emailed, so share their link from the patient list.' : ''}
        </p>
      </Card>

      <div className="two-col">
        <Card>
          <h2>One patient</h2>
          <label className="field">
            <span>Full name</span>
            <input type="text" value={name} maxLength={60} autoComplete="off" placeholder="Linda Chen" onChange={(e) => setName(e.target.value)} />
          </label>
          <label className="field">
            <span>Email address</span>
            <input type="email" value={email} maxLength={254} autoComplete="off" placeholder="linda.chen@example.com" onChange={(e) => setEmail(e.target.value)} />
          </label>
          <button type="button" className="primary" disabled={busy !== null || !oneValid || doctors.length === 0} onClick={() => void submit([{ name: name.trim(), email: email.trim() }], 'one')}>
            {busy === 'one' ? 'Adding…' : invite ? 'Add and invite' : 'Add to the list'}
          </button>
        </Card>

        <Card>
          <h2>A list</h2>
          <p className="hint small">Paste from a spreadsheet, or choose a CSV file. One patient per line: a name and an email address.</p>
          <textarea value={bulk} rows={6} placeholder={EXAMPLE} spellCheck={false} onChange={(e) => setBulk(e.target.value)} />
          <label className="field">
            <span>Or a CSV file</span>
            <input
              type="file"
              accept=".csv,.txt,text/csv,text/plain"
              onChange={async (e) => {
                const f = e.target.files?.[0];
                if (f) setBulk(await f.text());
                e.target.value = '';
              }}
            />
          </label>
          {bulk.trim() ? (
            <p className="hint small">
              {parsed.patients.length} ready
              {parsed.problems.length > 0 ? `, ${parsed.problems.length} ${parsed.problems.length === 1 ? 'line' : 'lines'} skipped` : ''}
            </p>
          ) : null}
          {parsed.problems.length > 0 ? (
            <ul className="result-list">
              {parsed.problems.slice(0, 8).map((p) => (
                <li key={p.line} className="warn">
                  Line {p.line}: {p.reason} <span className="hint">({p.text.slice(0, 60)})</span>
                </li>
              ))}
            </ul>
          ) : null}
          <button type="button" className="primary" disabled={busy !== null || parsed.patients.length === 0 || doctors.length === 0} onClick={() => void submit(parsed.patients, 'many')}>
            {busy === 'many' ? 'Adding…' : `${invite ? 'Add and invite' : 'Add'} ${parsed.patients.length || ''} ${parsed.patients.length === 1 ? 'patient' : 'patients'}`}
          </button>
        </Card>
      </div>

      {error ? <p className="form-error">{error}</p> : null}

      {result ? (
        <Card>
          <h2>Result</h2>
          <ul className="result-list">
            {result.added.map((e) => {
              const inv = result.invited.find((i) => i.entry.id === e.id);
              return (
                <li key={e.id}>
                  <StatusBadge status={e.status} /> <strong>{e.name}</strong> <span className="hint">{e.email}</span>
                  {inv ? <span className={inv.email.sent ? 'good' : 'warn'}> · {inv.email.sent ? 'invitation emailed' : 'invitation not emailed'}</span> : null}
                  {inv && !inv.email.sent ? <LinkBox link={inv.link} say={say} /> : null}
                </li>
              );
            })}
            {result.skipped.map((s, i) => (
              <li key={`skip-${i}`} className="warn">
                Skipped {s.name || s.email}: {s.reason}
              </li>
            ))}
          </ul>
          <a className="button secondary small" href="#/practice">
            See the patient list
          </a>
        </Card>
      ) : null}

      <p className="hint small">
        Already have a patient's Smart Agent address? <a href="#/practice/enroll">Enroll them directly</a> instead.
      </p>
    </>
  );
}
